/**
 * One overlay rectangle painted on a page's overlay layer.
 *
 * Positioning + sizing comes from the canonical `documentRectToView`
 * transform — overlays store their geometry in document space (CSS
 * pixels at scale=1, rotation=0) and the viewer applies scale/rotation
 * before paint. That means a stored rect stays pinned to the same
 * spot on the page through zoom, resize, fit-mode swaps, and rotation
 * without any per-caller math.
 *
 * Each kind opts into a tone class. We deliberately use Tailwind
 * utility classes (vs. inline styles) so the design tokens stay in
 * `index.css` and dark mode "just works".
 */

import { forwardRef, type CSSProperties, type KeyboardEvent } from 'react'

import { documentRectToView } from '../coordinates'
import type { PageDimensions, Rotation } from '../types'
import { cn } from '@/lib/utils'
import type {
  OverlayActivateEvent,
  OverlayItemModel,
  OverlayKind,
  OverlayTone,
} from './types'

export interface OverlayItemProps {
  item: OverlayItemModel
  /**
   * Page dimensions (intrinsic) so the rect can be re-projected when
   * the user rotates the viewer.
   */
  pageDimensions: PageDimensions
  scale: number
  rotation: Rotation
  selected: boolean
  focused: boolean
  /** Tab stop hint for roving-tabindex layers. */
  tabStop: boolean
  onSelect?: (event: OverlayActivateEvent) => void
  onActivate?: (event: OverlayActivateEvent) => void
  /** Notifies the layer when DOM focus lands on the item. */
  onFocus?: (id: string) => void
  /** Optional override: when the layer is read-only, lock pointer events. */
  interactive?: boolean
  className?: string
}

const KIND_TONE_DEFAULT: Record<OverlayKind, OverlayTone> = {
  highlight: 'warning',
  field: 'primary',
  'edit-target': 'info',
  annotation: 'neutral',
}

const TONE_CLASSES: Record<OverlayTone, { base: string; selected: string }> = {
  neutral: {
    base: 'border-border bg-border/20',
    selected: 'border-foreground bg-foreground/20',
  },
  primary: {
    base: 'border-primary/60 bg-primary/10',
    selected: 'border-primary bg-primary/25',
  },
  success: {
    base: 'border-success-soft bg-success-soft/40',
    selected: 'border-success bg-success-soft/70',
  },
  warning: {
    base: 'border-warning-soft bg-warning-soft/40',
    selected: 'border-warning bg-warning-soft/70',
  },
  danger: {
    base: 'border-destructive/60 bg-destructive/10',
    selected: 'border-destructive bg-destructive/25',
  },
  info: {
    base: 'border-info-soft bg-info-soft/40',
    selected: 'border-info bg-info-soft/70',
  },
}

function isSelectable(item: OverlayItemModel): boolean {
  if (item.selectable === false) return false
  if (item.selectable === true) return true
  return item.kind !== 'highlight'
}

function isActivatable(item: OverlayItemModel): boolean {
  if (item.activatable === false) return false
  if (item.activatable === true) return true
  // Fields and edit targets are activatable by default.
  return item.kind === 'field' || item.kind === 'edit-target'
}

/**
 * Renders a single overlay box.
 *
 * `forwardRef` lets the OverlayLayer manage the roving-tabindex
 * imperatively — when keyboard focus moves to a new item, the layer
 * calls `el.focus()` on the next stop.
 */
export const OverlayItem = forwardRef<HTMLButtonElement, OverlayItemProps>(
  function OverlayItem(
    {
      item,
      pageDimensions,
      scale,
      rotation,
      selected,
      focused,
      tabStop,
      onSelect,
      onActivate,
      onFocus,
      interactive = true,
      className,
    },
    ref,
  ) {
    const projected = documentRectToView(item.rect, pageDimensions, scale, rotation)
    const tone = item.tone ?? KIND_TONE_DEFAULT[item.kind]
    const toneClasses = TONE_CLASSES[tone]
    const selectable = isSelectable(item)
    const activatable = isActivatable(item)
    const canInteract = interactive && selectable

    const style: CSSProperties = {
      position: 'absolute',
      left: `${projected.x}px`,
      top: `${projected.y}px`,
      // Floor the size by 0.5px so adjacent overlays don't double-paint
      // borders when their rects share an edge (common in dense form
      // layouts).
      width: `${Math.max(2, projected.width)}px`,
      height: `${Math.max(2, projected.height)}px`,
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
      if (!canInteract) return
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        onSelect?.({ id: item.id, source: 'keyboard' })
        if (activatable) onActivate?.({ id: item.id, source: 'keyboard' })
      }
    }

    const handleClick = () => {
      if (!canInteract) return
      onSelect?.({ id: item.id, source: 'pointer' })
    }

    const handleDoubleClick = () => {
      if (!canInteract || !activatable) return
      onActivate?.({ id: item.id, source: 'pointer' })
    }

    const handleFocus = () => {
      if (!canInteract) return
      onFocus?.(item.id)
    }

    const ariaLabel =
      item.label ?? `${item.kind} overlay on page ${item.pageIndex + 1}`

    return (
      <button
        ref={ref}
        type="button"
        tabIndex={canInteract ? (tabStop ? 0 : -1) : -1}
        aria-label={ariaLabel}
        aria-description={item.description}
        aria-pressed={selectable ? selected : undefined}
        aria-current={focused && canInteract ? 'true' : undefined}
        data-overlay-id={item.id}
        data-overlay-kind={item.kind}
        data-overlay-tone={tone}
        data-overlay-selected={selected ? 'true' : 'false'}
        data-overlay-focused={focused ? 'true' : 'false'}
        data-testid="viewer-overlay-item"
        disabled={!canInteract}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        style={style}
        className={cn(
          // Reset native button look; the toolbar `Button` component is
          // not appropriate here because it ships padding + min-size that
          // would distort overlay rectangles.
          'block cursor-pointer rounded-[3px] border-2 p-0 text-left transition-[box-shadow,background-color,border-color]',
          canInteract ? 'pointer-events-auto' : 'pointer-events-none',
          toneClasses.base,
          selected && toneClasses.selected,
          focused && canInteract && 'shadow-[0_0_0_2px_var(--color-ring)]',
          // Pure highlights are read-only — flatten the cursor + hover.
          !canInteract && 'cursor-default',
          className,
        )}
      >
        {item.label && (
          <span
            data-testid="viewer-overlay-label"
            className={cn(
              // Floating label chip in the rect's top-left so dense
              // forms stay legible. Hidden when the rect is too small.
              'pointer-events-none absolute -top-5 left-0 max-w-[100%] truncate rounded-sm border border-border bg-background/95 px-1.5 py-0.5 text-[10px] font-medium leading-none text-foreground shadow-soft',
              projected.height < 18 && 'hidden',
            )}
          >
            {item.label}
          </span>
        )}
      </button>
    )
  },
)
